import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { INDI_CUSTOMERS_URL, SALESORDERS_SERVICE_URL } from 'src/app/injection-tokens';
import { forkJoin, Observable } from 'rxjs';
import { IndiCustomer } from '../indi-customer';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { ApiResponse } from '../api-response';
import { APIVerkooporders } from '../APIVerkooporders';
import { APIVerkoopordersToWrite } from '../APIVerkoopordersToWrite';
import { User } from '../user';
import { ICMCustomerNotes } from '../ICMCustomerNotes';
import { ManuallyCreatedRequest } from '../manually-created-request';

export interface CombinedData {
  APIVerkooporders: APIVerkooporders;
  indiCustomer: IndiCustomer;
  users: User[];
  ICMCustomerNotes: ICMCustomerNotes;
}

@Injectable({
  providedIn: 'root',
})
export class NewSalesorderService {
  constructor(
    @Inject(SALESORDERS_SERVICE_URL) readonly salesordersServiceURL: string,
    @Inject(INDI_CUSTOMERS_URL) readonly indiCustomersUrl: string,
    readonly http: HttpClient
  ) {}

  getCombinedData(ids: number | number[]): Observable<CombinedData> {
    const idArray = Array.isArray(ids) ? ids : [ids];
    return this.getAPIVerkooporders(idArray).pipe(
      switchMap((APIVerkooporders: APIVerkooporders) => {
        const customerNumber = APIVerkooporders.verkooporder.customerNumber;
        return forkJoin({
          indiCustomers: this.getIndiCustomers(customerNumber),
          users: this.getUsers(customerNumber),
          ICMCustomerNotes: this.getIcmNote(customerNumber)
        }).pipe(
          map(({ indiCustomers, users, ICMCustomerNotes }) => ({
            APIVerkooporders,
            indiCustomer: indiCustomers[0],
            users,
            ICMCustomerNotes
          }))
        );
      }),
      shareReplay(1)
    );
  }

  getAPIVerkooporders(ids: number[]): Observable<APIVerkooporders> {
    let url = this.salesordersServiceURL + '?id=';
    url += ids.join('&id=');
    return this.http.get<APIVerkooporders>(url);
  }

  getIndiCustomers(customerNumber: string): Observable<IndiCustomer[]> {
    return this.http.get<IndiCustomer[]>(
      this.indiCustomersUrl + '?customerNumber=' + customerNumber + '&fetchSegments=true'
    );
  }

  getUsers(customerNumber: string): Observable<User[]> {
    return this.http.get<User[]>(this.indiCustomersUrl + '/' + customerNumber + '/user-email-preferences');
  }

  getIcmNote(customerNumber: string): Observable<ICMCustomerNotes> {
    return this.http.get<ICMCustomerNotes>(this.indiCustomersUrl + '/' + customerNumber + '/notes');
  }

  postAPIVerkoopordersToWrite(salesorders: APIVerkoopordersToWrite): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(
      this.salesordersServiceURL,
      salesorders
    );
  }

  markVerkoopordersAsManuallyCreated(request: ManuallyCreatedRequest): Observable<void> {
    return this.http.patch<void>(
      `${this.salesordersServiceURL}/manuallycreated`,
      request
    );
  }
}
